function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute("form", `${element.form.getAttribute("id")}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (elementToFocus.tagName === "INPUT" && ["search", "text", "email", "url"].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ["ARROWUP", "ARROWDOWN", "ARROWLEFT", "ARROWRIGHT", "TAB", "ENTER", "SPACE", "ESCAPE", "HOME", "END", "PAGEUP", "PAGEDOWN"];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

/**
 * Product Total Price
 * Updates the main product price display when quantity changes in tiered pricing
 * Listens for quantity:change events and recalculates total pricing
 */
if (!customElements.get("product-total-price")) {
  customElements.define(
    "product-total-price",
    class ProductTotalPrice extends HTMLElement {
      constructor() {
        super();
        this.productId = null;
        this.currentQuantity = 1;
        this.discountTiers = [];
        this.isSubscriptionActive = false;
        this.calculatedOnetimePrice = null;
        this.calculatedSubscriptionPrice = null;
        this.variantChangeUnsubscriber = undefined;
      }

      connectedCallback() {
        // Read data attributes (must be in connectedCallback, not constructor)
        this.sectionId = this.dataset.sectionId;
        this.basePrice = parseFloat(this.dataset.basePrice);
        this.subscriptionPrice = parseFloat(this.dataset.subscriptionPrice);
        this.hasSelling = this.dataset.hasSellingPlans === "true";
        this.subscriptionDiscount = parseFloat(this.dataset.discountPercentage || "0");
        this.showMembershipSavings = this.dataset.showMembershipSavings === "true";

        // Get price elements
        this.priceCurrentEl = this.querySelector(".price-item--current");
        this.priceCurrentContainer = this.querySelector(".price__current");
        this.priceSubscriptionContainer = this.querySelector(".price__subscription");
        this.priceSubscriptionEl = this.querySelector(".price-item--subscription");
        this.priceDiscountBadge = this.querySelector(".price__discount-badge");
        this.priceDiscountBadge2 = this.querySelector(".price__discount-badge2");
        this.discountPercentageEl = this.querySelector(".discount-percentage");
        this.discountPercentageEl2 = this.querySelector(".discount-percentage2");
        this.priceOnetimeLabel = this.querySelector(".price__onetime-label");
        this.priceOnetimeEl = this.querySelector(".price-item--onetime");

        // Listen for quantity changes from tiered pricing
        document.addEventListener("quantity:change", this.handleQuantityChange.bind(this));

        // Listen for purchase type changes (one-time vs subscription)
        document.addEventListener("purchase-type:change", this.handlePurchaseTypeChange.bind(this));

        // Listen for variant changes to re-sync subscription state
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));

        // Try to get product ID from tiered pricing component
        this.getTieredPricingData();

        // Show membership savings by default if setting is enabled and product has selling plans
        if (this.showMembershipSavings && this.hasSelling) {
          this.isSubscriptionActive = true;
          this.updatePriceDisplay();
        }
      }

      disconnectedCallback() {
        document.removeEventListener("quantity:change", this.handleQuantityChange.bind(this));
        document.removeEventListener("purchase-type:change", this.handlePurchaseTypeChange.bind(this));
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      getTieredPricingData() {
        const tieredPricing = document.querySelector("quantity-tiered-pricing");
        if (tieredPricing) {
          this.productId = tieredPricing.dataset.productId;
          this.subscriptionDiscount = parseFloat(tieredPricing.dataset.subscriptionDiscount || 0);
          this.fetchDiscounts();
        }
      }

      handleQuantityChange(event) {
        if (event.detail.sectionId === this.sectionId) {
          this.currentQuantity = event.detail.quantity;

          // If event includes pre-calculated prices, use those
          if (event.detail.onetimePrice !== undefined && event.detail.subscriptionPrice !== undefined) {
            this.calculatedOnetimePrice = event.detail.onetimePrice;
            this.calculatedSubscriptionPrice = event.detail.subscriptionPrice;
          } else {
            // Clear calculated prices to fall back to own calculation
            this.calculatedOnetimePrice = null;
            this.calculatedSubscriptionPrice = null;
          }

          this.updatePriceDisplay();
        }
      }

      handlePurchaseTypeChange(event) {
        if (event.detail.sectionId === this.sectionId) {
          this.isSubscriptionActive = event.detail.isSubscription;
          this.updatePriceDisplay();
        }
      }

      handleVariantChange(event) {
        if (event.data?.sectionId !== this.sectionId) return;

        // Update base price from new variant
        if (event.data.variant?.price) {
          this.basePrice = event.data.variant.price;
        }

        // Reset calculated prices so updatePriceDisplay() recalculates with new basePrice
        this.calculatedOnetimePrice = null;
        this.calculatedSubscriptionPrice = null;

        // Re-query current subscription state from purchase-type-selector
        const purchaseTypeSelector = document.querySelector(`purchase-type-selector[data-section-id="${this.sectionId}"]`);

        if (purchaseTypeSelector) {
          const subscriptionRadio = purchaseTypeSelector.querySelector('input[type="radio"][value="subscription"]');
          this.isSubscriptionActive = subscriptionRadio?.checked ?? false;
        }

        // Re-cache price elements (innerHTML was replaced)
        this.priceCurrentEl = this.querySelector(".price-item--current");
        this.priceCurrentContainer = this.querySelector(".price__current");
        this.priceSubscriptionContainer = this.querySelector(".price__subscription");
        this.priceSubscriptionEl = this.querySelector(".price-item--subscription");
        this.priceDiscountBadge = this.querySelector(".price__discount-badge");
        this.priceDiscountBadge2 = this.querySelector(".price__discount-badge2");
        this.discountPercentageEl = this.querySelector(".discount-percentage");
        this.discountPercentageEl2 = this.querySelector(".discount-percentage2");
        this.priceOnetimeLabel = this.querySelector(".price__onetime-label");
        this.priceOnetimeEl = this.querySelector(".price-item--onetime");

        this.updatePriceDisplay();
      }

      async fetchDiscounts() {
        if (!this.productId) {
          return;
        }

        this.discountTiers = await fetchQuantityDiscounts(this.productId);
        this.updatePriceDisplay();
      }

      updatePriceDisplay() {
        if (!this.priceCurrentEl) {
          return;
        }

        // Use pre-calculated prices if available, otherwise calculate here
        let onetimeTotal;
        let subscriptionTotal;

        if (this.calculatedOnetimePrice !== null && this.calculatedOnetimePrice !== undefined) {
          // Use prices calculated by quantity-input
          onetimeTotal = this.calculatedOnetimePrice;
          subscriptionTotal = this.calculatedSubscriptionPrice;
        } else {
          // Fall back to own calculation (for tiered-pricing or other components)
          const quantityDiscount = getApplicableDiscount(this.discountTiers, this.currentQuantity);
          onetimeTotal = this.basePrice * this.currentQuantity * (1 - quantityDiscount);
          const subscriptionDiscountMultiplier = 1 - this.subscriptionDiscount / 100;
          subscriptionTotal = onetimeTotal * subscriptionDiscountMultiplier;
        }

        // Split Price Niklas has its own two-column layout (member price, strikethrough
        // compare, savings row). The generic branches below only know the standard price
        // layout and would hide the member column, so delegate and stop here.
        const splitPrice = this.querySelector(".niklas-split-price");
        if (splitPrice) {
          updateNiklasSplitPrice({
            splitPrice,
            basePrice: this.basePrice,
            quantity: this.currentQuantity,
            onetimePrice: onetimeTotal,
          });
          return;
        }

        // Update the display based on purchase type
        if (this.isSubscriptionActive && this.hasSelling) {
          // Show discount badge with dynamic percentage
          if (this.priceDiscountBadge) {
            this.priceDiscountBadge.classList.remove("hidden");
          }
          if (this.priceDiscountBadge2) {
            this.priceDiscountBadge2.classList.remove("hidden");
          }
          if (this.discountPercentageEl) {
            this.discountPercentageEl.textContent = Math.round(this.subscriptionDiscount);
          }
          if (this.discountPercentageEl2) {
            this.discountPercentageEl2.textContent = Math.round(this.subscriptionDiscount);
          }

          // Show one-time price as main price
          this.priceCurrentEl.textContent = formatPriceDKK(onetimeTotal);
          if (this.priceCurrentContainer) {
            this.priceCurrentContainer.classList.remove("hidden");
          }

          // Show membership benefit price below
          if (this.priceSubscriptionEl) {
            this.priceSubscriptionEl.textContent = formatPriceDKK(subscriptionTotal);
          }
          if (this.priceSubscriptionContainer) {
            this.priceSubscriptionContainer.classList.remove("hidden");
          }

          // Hide Engangskøb label (no longer used)
          if (this.priceOnetimeLabel) {
            this.priceOnetimeLabel.classList.add("hidden");
          }
        } else {
          // Hide discount badges
          if (this.priceDiscountBadge) {
            this.priceDiscountBadge.classList.add("hidden");
          }
          if (this.priceDiscountBadge2) {
            this.priceDiscountBadge2.classList.add("hidden");
          }

          // Hide subscription price
          if (this.priceSubscriptionContainer) {
            this.priceSubscriptionContainer.classList.add("hidden");
          }

          // Hide Engangskøb label
          if (this.priceOnetimeLabel) {
            this.priceOnetimeLabel.classList.add("hidden");
          }

          // Show one-time price normally
          this.priceCurrentEl.textContent = formatPriceDKK(onetimeTotal);
          // The Split Price Niklas block sizes this element with a scoped rule that needs
          // the `niklas-split-price__current` class. Overwriting className drops that class
          // and the configured size/weight/color is lost — so skip the reset for it.
          if (!this.priceCurrentEl.classList.contains("niklas-split-price__current")) {
            this.priceCurrentEl.className = "price-item price-item--current text-xl font-semibold text-neutral-700";
          }
          if (this.priceCurrentContainer) {
            this.priceCurrentContainer.classList.remove("hidden");
          }
        }
      }
    },
  );
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return (...args) => {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = (fn, scope) => () => fn.apply(scope, arguments);

Shopify.setSelectorByValue = (selector, value) => {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = (target, eventName, callback) => {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = (path, options) => {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: (selector) => {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: (selector, values) => {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)").forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset = this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace("shopify-section-", "");
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const sourceQty = html.querySelector(".quick-order-list-container").parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`));
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == "VIDEO" && deferredElement.getAttribute("autoplay")) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute("style");
      deferredElement.setAttribute("style", "display: block;");
      window.setTimeout(() => {
        deferredElement.setAttribute("style", formerStyle);
      }, 0);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target.closest(".product-form__input").querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty("--swatch-focal-point", target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset");
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener("storefront:signincompleted", this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace("[min]", event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace("[max]", event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace("[step]", event.target.step));
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      event.target.setAttribute("value", inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

class ProductUpsell extends HTMLElement {
  constructor() {
    super();
    this.modal = document.querySelector("#UpsellModal");
    this.template = document.querySelector("#UpsellTemplate")?.innerHTML;
    this.emojiConfig = [
      { unicode: "U+1F600", char: "😀" },
      { unicode: "U+1F603", char: "😃" },
      { unicode: "U+1F604", char: "😄" },
      { unicode: "U+1F605", char: "😅" },
      { unicode: "U+1F602", char: "😂" },
      { unicode: "U+1F923", char: "🤣" },
      { unicode: "U+1F61B", char: "😛" },
      { unicode: "U+1F62C", char: "😬" },
      { unicode: "U+1F60D", char: "😍" },
      { unicode: "U+1F970", char: "🥰" },
      { unicode: "U+1F618", char: "😘" },
      { unicode: "U+1F60B", char: "😋" },
      { unicode: "U+1F44D", char: "👍" },
      { unicode: "U+270C", char: "✌" },
      { unicode: "U+1F91F", char: "🤟" },
      { unicode: "U+1F44A", char: "👊" },
      { unicode: "U+1F496", char: "💖" },
      { unicode: "U+1F495", char: "💕" },
      { unicode: "U+2B50", char: "⭐" },
      { unicode: "U+2728", char: "✨" },
      { unicode: "U+26BD", char: "⚽" },
      { unicode: "U+1F3C0", char: "🏀" },
      { unicode: "U+1F3C8", char: "🏈" },
      { unicode: "U+26BE", char: "⚾" },
      { unicode: "U+1F47E", char: "👾" },
      { unicode: "U+1F984", char: "🦄" },
      { unicode: "U+1F47B", char: "👻" },
      { unicode: "U+1F916", char: "🤖" },
      { unicode: "U+1F43C", char: "🐼" },
      { unicode: "U+1F431", char: "🐱" },
      { unicode: "U+1F47D", char: "👽" },
      { unicode: "U+1F480", char: "💀" },
      { unicode: "U+1F42F", char: "🐯" },
      { unicode: "U+1F479", char: "👹" },
      { unicode: "U+1F435", char: "🐵" },
      { unicode: "U+1F436", char: "🐶" },
      { unicode: "U+1F4A9", char: "💩" },
      { unicode: "U+1F434", char: "🐴" },
      { unicode: "U+1F411", char: "🐑" },
      { unicode: "U+1F407", char: "🐇" },
    ];
    this.app = null;
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.app) {
      this.app.unmount();
      this.app = null;
    }
  }

  handleCartUpdate(event) {
    if (event.source === "product-form" && event.cartData) {
      const productHandle = event.cartData.handle;
      const allowCustomLabel = event.cartData.properties?._allow_custom_label === "true";
      this.open(productHandle, event.cartData, false, allowCustomLabel);
    }
  }

  async fetchProductData(handle) {
    return fetch(`/products/${handle}`)
      .then((response) => {
        if (response.ok) return response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const productJsonScript = doc.querySelector("#ProductJSON");
        if (productJsonScript) return JSON.parse(productJsonScript.innerHTML);
        throw new Error("Failed to find product JSON script");
      })
      .catch((error) => {
        console.error("Error in fetchProductJSON:", error);
        throw error;
      });
  }

  async open(productHandle, cartData, productJson = false, allowCustomLabel = false) {
    this.productJson = productJson || (await this.fetchProductData(productHandle));

    if (!cartData || !this.productJson) {
      console.error("Failed to get product data whilst opening upsell modal.");
      return;
    }

    if (this.app) {
      this.app.unmount();
      this.app = null;
    }

    let lineItem = null;
    let bundleItem = null;

    if (cartData?.items) {
      cartData.items.forEach((item) => {
        if (item.properties._type === "Food Bag Combo") {
          bundleItem = item;
        } else {
          lineItem = item;
        }
      });
    } else {
      lineItem = cartData;
    }

    this.initializeApp(lineItem, bundleItem, allowCustomLabel);
    this.modal?.open();
  }

  initializeApp(lineItem, bundleItem = null, allowCustomLabel) {
    const createApp = Vue?.createApp;
    if (!createApp) {
      console.error("Vue is not loaded");
      return;
    }

    this.app = createApp(this.vueApp(lineItem, bundleItem, this.productJson, this.template, this.emojiConfig, allowCustomLabel));
    this.app.mount(this);
  }

  vueApp(cartData, bundleData = null, productData, template, emojiConfig, allowCustomLabel = false) {
    const { ref, computed } = Vue;

    return {
      template,
      delimiters: ["${", "}"],
      setup() {
        const cartDataRef = ref(cartData);
        const productDataRef = ref(productData);
        const isCustomLabelAllowed = ref(allowCustomLabel);
        const emojis = ref(emojiConfig);
        const isScreen = ref("main");
        const labelKey = ref("Name");

        const linePrice = computed(() => (currency = "DKK") => {
          const price = cartDataRef.value["line_price"];
          return theme.utils.formatMoney(price, `{{amount_with_space_separator}} ${currency}`);
        });

        const formatPrice = computed(
          () =>
            (amount, currency = "DKK") =>
              theme.utils.formatMoney(amount, `{{amount_with_space_separator}} ${currency}`),
        );

        const lineQuantity = ref(cartDataRef.value.quantity ?? 1);

        const columnCount = computed(() => {
          if (!isSubscriptionAvailable.value) return 0;
          if (isCustomLabelAllowed.value && isSubscriptionUnset.value) return 2;
          return 1;
        });

        const isSubscriptionAvailable = computed(() => {
          return (productDataRef.value?.selling_plan_groups?.length ?? 0) > 0;
        });

        const isSubscriptionUnset = computed(() => {
          return !cartDataRef.value?.selling_plan_allocation;
        });

        const selectedSellingPlan = ref(productDataRef.value?.selling_plan_groups[0]?.selling_plans[0]?.id ?? null);

        const sellingPlanGroups = computed(() => {
          return productDataRef.value?.selling_plan_groups[0]?.selling_plans ?? false;
        });

        const subscriptionPrice = computed(() => {
          const linePrice = cartDataRef.value["line_price"];
          const plans = sellingPlanGroups.value;
          let selectedPlan = plans.find((plan) => plan.id === selectedSellingPlan.value);
          if (!selectedPlan && plans.length) {
            selectedPlan = plans[0];
          }
          const discount = selectedPlan.price_adjustments[0].value;
          return theme.utils.formatMoney(linePrice - linePrice * (discount / 100), "{{amount_with_space_separator}} kr");
        });

        const lineItemLabelValue = computed(() => {
          return cartDataRef.value?.properties[labelKey.value] ?? "";
        });

        const labelValue = ref("");

        function clearLabel() {
          labelValue.value = "";
        }

        const selectedVariant = computed(() => {
          return productDataRef.value.variants.find((variant) => variant.id == cartDataRef.value.variant_id);
        });

        function updateCartData(newCartData) {
          cartDataRef.value = newCartData;
        }

        function setScreen(screen) {
          isScreen.value = screen;
        }

        function closeModal() {
          document.querySelector("#UpsellModal")?.close();
        }

        return {
          cartData: cartDataRef,
          productData: productDataRef,
          linePrice,
          formatPrice,
          lineQuantity,
          changeQuantity: (quantity) => {
            if (typeof quantity !== "number") {
              lineQuantity.value = cartDataRef.value.quantity;
              return;
            }

            const updateData = {
              id: cartDataRef.value.key,
              quantity: Number(quantity),
            };

            fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(updateData),
            })
              .then((response) => response.json())
              .then((cart) => {
                const viewKey = cart?.items_added[0]?.view_key ?? false;
                const updatedItem = cart.items.find((item) => {
                  if (viewKey && item.key === viewKey) return item;
                  if (item.url === cartDataRef.value.url && item.quantity === quantity) return item;
                  return null;
                });

                lineQuantity.value = quantity;
                if (updatedItem) updateCartData(updatedItem);
                setScreen("main");
              })
              .catch((error) => {
                console.error("Error updating cart:", error);
              });
          },
          isSubscriptionAvailable,
          isSubscriptionUnset,
          sellingPlanGroups,
          subscriptionPrice,
          selectedVariant,
          selectedSellingPlan,
          columnCount,
          openCart: () => {
            closeModal();
            if (window?.Rebuy?.SmartCart) {
              window.Rebuy.SmartCart.show();
            } else {
              const cartDrawer = document.querySelector("#CartDrawer");
              if (cartDrawer && typeof cartDrawer.open === "function") {
                cartDrawer.open();
              }
            }
          },
          handleUpgrade: () => {
            const { key, quantity } = cartDataRef.value;
            const updateData = {
              id: key,
              quantity: quantity,
              selling_plan: selectedSellingPlan.value,
            };

            fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(updateData),
            })
              .then((response) => response.json())
              .then((cart) => {
                const updatedItem = cart.items.find((item) => {
                  const itemPlanId = item?.selling_plan_allocation?.selling_plan?.id;
                  if (item.key === cartDataRef.value.key) return item;
                  if (item.id === cartDataRef.value.id && itemPlanId === selectedSellingPlan.value && item.quantity === quantity) return item;
                  return undefined;
                });

                if (updatedItem) updateCartData(updatedItem);
              })
              .catch((error) => {
                console.error("Error updating cart:", error);
              });
          },
          removeItem: () => {
            const key = cartDataRef.value["key"];
            fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: key, quantity: 0 }),
            })
              .then((response) => response.json())
              .then(() => {
                closeModal();
              })
              .catch((error) => {
                console.error("Error updating cart:", error);
              });
          },
          isCustomLabelAllowed,
          emojis,
          labelValue,
          lineItemLabelValue,
          cancelLabelUpdate: () => {
            clearLabel();
            setScreen("main");
          },
          clearLabel,
          saveLabel: () => {
            const { key, url, quantity } = cartDataRef.value;
            const updateData = {
              id: key,
              quantity: quantity,
              properties: { [labelKey.value]: labelValue.value },
            };

            fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(updateData),
            })
              .then((response) => response.json())
              .then((cart) => {
                const updatedItem = cart.items.find(({ url: itemUrl, properties }) => {
                  return itemUrl === url && properties[labelKey.value] === labelValue.value;
                });

                if (updatedItem) updateCartData(updatedItem);
                setScreen("main");
              })
              .catch((error) => {
                console.error("Error updating cart:", error);
              });
          },
          addEmoji: (emoji) => {
            if (labelValue.value.length < 20) {
              labelValue.value += emoji;
            }
          },
          setScreen,
          isScreen,
          closeModal,
        };
      },
    };
  }
}

customElements.define("product-upsell", ProductUpsell);
